import { apiClient } from './client'
import type { CursorPage } from './accounts'
import type { DashboardFilter, DashboardManifestResponse, WidgetKey, WidgetResult } from '../types/insights'
import type { ProgramFilingListView } from '../types/programFiling'

/**
 * Typed fetch wrappers for `module.insights` (IB-45) — same shape as
 * `api/accounts.ts`: a thin layer over `apiClient`, no fetch calls anywhere
 * else in the insights screens.
 */

function toQuery(filter: DashboardFilter): string {
  const q = new URLSearchParams()
  const entries = Object.entries(filter) as Array<[keyof DashboardFilter, string | undefined]>
  for (const [key, value] of entries) {
    if (value !== undefined && value !== null && value !== '') q.set(key, value)
  }
  return q.toString()
}

export async function getDashboardManifest(
  filter: DashboardFilter,
  signal?: AbortSignal,
): Promise<DashboardManifestResponse> {
  const qs = toQuery(filter)
  const { data } = await apiClient.get<DashboardManifestResponse>(
    `/insights/dashboard${qs ? `?${qs}` : ''}`,
    { signal },
  )
  return data
}

export async function getDashboardWidget<T>(
  widgetKey: WidgetKey,
  filter: DashboardFilter,
  signal?: AbortSignal,
): Promise<WidgetResult<T>> {
  const qs = toQuery(filter)
  const { data } = await apiClient.get<WidgetResult<T>>(
    `/insights/widgets/${widgetKey}${qs ? `?${qs}` : ''}`,
    { signal },
  )
  return data
}

export interface ListProgramFilingsParams {
  /** RSQL, already combined by the caller (see lib/rsql.ts). */
  filter?: string | null
  sort?: string | null
  cursor?: string | null
  limit?: number
}

/** AC12 drill target — backs `screens/cases/FilingsListScreen.tsx`. */
export async function listProgramFilings(
  params: ListProgramFilingsParams,
  signal?: AbortSignal,
): Promise<CursorPage<ProgramFilingListView>> {
  const q = new URLSearchParams()
  if (params.filter) q.set('filter', params.filter)
  if (params.sort) q.set('sort', params.sort)
  if (params.cursor) q.set('cursor', params.cursor)
  q.set('limit', String(params.limit ?? 50))
  const { data } = await apiClient.get<CursorPage<ProgramFilingListView>>(
    `/program-filings?${q.toString()}`,
    { signal },
  )
  return data
}
